import { readSqliteRows, SqliteReadError, type SqliteValue } from "./sqlite";
import type {
  LegacyHighlight,
  LegacyIssue,
  LegacyPreferences,
  LegacyRejectCode,
  LegacyVerseRef,
} from "./types";

/**
 * Reads the TWO real legacy origins.
 *
 * A. `databases/highlight` (SQLite): table `color(colorid, color)` + table
 *    `highlight(bookid, chapter, verse, color)` with PK `(bookid, chapter, verse)`.
 *    The equivalent query from the old app was:
 *      `SELECT H.bookid, H.chapter, H.verse, C.color FROM highlight H
 *       INNER JOIN color C ON H.color = C.colorid`
 *    **There is no date column**: the old app's "date order" was
 *    `ORDER BY H.rowid DESC`, not a timestamp.
 *
 * B. `files/persistStore/persist-rootTest5` (redux-persist): JSON where **each
 *    reducer is itself a JSON string**, so it must be un-nested.
 */

/** Target Bible version: the legacy `bookid` is canonical for the KJV. */
export const LEGACY_VERSION = "kjv";

/** Protestant canon. */
const MAX_BOOK = 66;
const MAX_CHAPTER = 150;
const MAX_VERSE = 176;

/** Canonical key for a coordinate. */
export function coordKey(ref: LegacyVerseRef): string {
  return `${ref.version}:${ref.book}:${ref.chapter}:${ref.verse}`;
}

/** Human-readable reference (`1:1:1`) for reports. */
export function refLabel(ref: Pick<LegacyVerseRef, "book" | "chapter" | "verse">): string {
  return `${ref.book}:${ref.chapter}:${ref.verse}`;
}

interface HighlightParseResult {
  highlights: LegacyHighlight[];
  issues: LegacyIssue[];
}

type HighlightParseOutcome =
  | ({ ok: true } & HighlightParseResult)
  | { ok: false; code: LegacyRejectCode; detail: string };

function asNumber(value: SqliteValue): number | undefined {
  return typeof value === "number" && Number.isInteger(value) ? value : undefined;
}

/**
 * Normalizes the legacy color to CSS.
 * Legacy stores it as hex **without** `#` (e.g. `FAA030`).
 *
 * Lowercase on purpose: that is how the new app writes its colors
 * (`HIGHLIGHT_PALETTE`), so a migrated highlight and one created here are
 * **byte-for-byte identical**. With uppercase letters, a migrated yellow and
 * the palette yellow would be two different colors in any literal comparison.
 * The legacy → app mapping is IDENTITY: each hex enters unchanged, without collapsing
 * to a subset (see `HIGHLIGHT_PALETTE`, the 10 pastels from the published app).
 */
function normalizeColor(raw: SqliteValue): string | undefined {
  if (typeof raw !== "string") {
    return undefined;
  }
  const value = raw.trim().replace(/^#/u, "");
  return /^[0-9a-f]{3,8}$/iu.test(value) ? `#${value.toLowerCase()}` : undefined;
}

/**
 * Reads highlights from the legacy SQLite database.
 *
 * Returns an explicit rejection (never zero rows in silence) if the file is
 * not SQLite, cannot be traversed, or lacks the expected tables.
 */
export function parseLegacyHighlightDb(bytes: Uint8Array): HighlightParseOutcome {
  let colorRows: Record<string, SqliteValue>[];
  let highlightRows: Record<string, SqliteValue>[];
  try {
    colorRows = readSqliteRows(bytes, "color");
    highlightRows = readSqliteRows(bytes, "highlight");
  } catch (error) {
    if (error instanceof SqliteReadError) {
      const missing = /not found/iu.test(error.message);
      return {
        ok: false,
        code: missing ? "missing-highlight-tables" : "unreadable-sqlite",
        detail: error.message,
      };
    }
    return { ok: false, code: "unreadable-sqlite", detail: String(error) };
  }

  // `color.colorid` → CSS color. The legacy JOIN is a logical FK, without a constraint.
  const palette = new Map<number, string>();
  for (const row of colorRows) {
    const id = asNumber(row.colorid ?? null);
    const color = normalizeColor(row.color ?? null);
    if (id !== undefined && color) {
      palette.set(id, color);
    }
  }

  const highlights: LegacyHighlight[] = [];
  const issues: LegacyIssue[] = [];
  const seen = new Set<string>();

  for (const row of highlightRows) {
    const book = asNumber(row.bookid ?? null);
    const chapter = asNumber(row.chapter ?? null);
    const verse = asNumber(row.verse ?? null);
    const at = `highlight[bookid=${String(row.bookid)},chapter=${String(row.chapter)},verse=${String(row.verse)}]`;

    if (book === undefined || chapter === undefined || verse === undefined) {
      issues.push({ code: "bad-row-shape", at, detail: "bookid/chapter/verse are not integers" });
      continue;
    }
    if (book < 1 || book > MAX_BOOK) {
      issues.push({ code: "book-out-of-range", at, detail: `bookid=${book}` });
      continue;
    }
    if (chapter < 1 || chapter > MAX_CHAPTER || verse < 1 || verse > MAX_VERSE) {
      issues.push({
        code: "chapter-or-verse-out-of-range",
        at,
        detail: `chapter=${chapter} verse=${verse}`,
      });
      continue;
    }

    const colorId = asNumber(row.color ?? null);
    const color = colorId === undefined ? undefined : palette.get(colorId);
    if (!color) {
      // The FK is logical: an orphan colorid is possible and must not be lost silently.
      issues.push({ code: "unknown-color", at, detail: `color=${String(row.color)}` });
      continue;
    }

    const ref: LegacyHighlight = { version: LEGACY_VERSION, book, chapter, verse, color };
    const key = coordKey(ref);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    highlights.push(ref);
  }

  return { ok: true, highlights, issues };
}

// ─── redux-persist: preferences ─────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * redux-persist stores `{ ui: "<json>", bible: "<json>", … }`: each reducer is
 * a STRING containing JSON. Un-nests one specific slice.
 */
function unnestSlice(root: Record<string, unknown>, key: string): Record<string, unknown> {
  const raw = root[key];
  if (isRecord(raw)) {
    return raw;
  }
  if (typeof raw !== "string") {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function pickInt(slice: Record<string, unknown>, key: string): number | undefined {
  const value = slice[key];
  return typeof value === "number" && Number.isInteger(value) ? value : undefined;
}

function pickBool(slice: Record<string, unknown>, key: string): boolean | undefined {
  const value = slice[key];
  return typeof value === "boolean" ? value : undefined;
}

function pickString(slice: Record<string, unknown>, key: string): string | undefined {
  const value = slice[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

type PreferencesParseOutcome =
  | { ok: true; preferences: LegacyPreferences }
  | { ok: false; code: LegacyRejectCode; detail: string };

/** Reads preferences from the legacy redux-persist dump. */
export function parseLegacyPreferences(text: string): PreferencesParseOutcome {
  let root: unknown;
  try {
    root = JSON.parse(text);
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    return { ok: false, code: "unknown-json", detail: `Not valid JSON: ${reason}` };
  }
  if (!isRecord(root)) {
    return { ok: false, code: "unknown-json", detail: "The JSON root is not an object" };
  }

  const bible = unnestSlice(root, "bible");
  const ui = unnestSlice(root, "ui");
  const dictionary = unnestSlice(root, "dictionary");

  const preferences: LegacyPreferences = {
    currentBook: pickInt(bible, "currentBook"),
    currentChapter: pickInt(bible, "currentChapter"),
    currentVerse: pickInt(bible, "currentVerse"),
    verseTextFontSize: pickInt(bible, "verseTextFontSize"),
    showStrongNumbers: pickBool(bible, "showStrongNumbers"),
    isOrderBooksByAlpha: pickBool(bible, "isOrderBooksByAlpha"),
    currentTheme: pickString(ui, "currentTheme"),
    lastStrongNumber: pickString(dictionary, "lastStrongNumber"),
  };

  const known = Object.values(preferences).some((value) => value !== undefined);
  if (!known) {
    return {
      ok: false,
      code: "unknown-json",
      detail: "No known Bible Strong preference was found in this file",
    };
  }
  return { ok: true, preferences };
}
